/**
 * A one-line reading of what a failed WebRTC handshake actually tried.
 *
 * The candidate types each side gathered say whether the two peers had a route
 * on paper. They do not say whether anything travelled it. A link can gather
 * host, srflx and relay on both sides, exchange every one of them, and still
 * fail — and then the only thing worth knowing is how many connectivity checks
 * went out on each kind of pair and how many came back. Zero responses on every
 * pair is a different fault from responses that arrive and are then rejected.
 *
 * Written for `RTCPeerConnection.getStats()`, whose report is Map-like on both
 * platforms (`RTCStatsReport` on web, a plain `Map` in react-native-webrtc).
 */

type StatsEntry = Record<string, unknown>;

type MapLike = {
  forEach: (callback: (value: unknown, key: unknown) => void) => void;
};

function isMapLike(report: unknown): report is MapLike {
  return (
    typeof report === 'object' &&
    report !== null &&
    typeof (report as MapLike).forEach === 'function'
  );
}

function str(entry: StatsEntry, key: string): string | undefined {
  const value = entry[key];
  return typeof value === 'string' ? value : undefined;
}

function num(entry: StatsEntry, key: string): number {
  const value = entry[key];
  return typeof value === 'number' ? value : 0;
}

/**
 * Collapse the candidate pairs into `relay/relay 12 sent 0 got 0` lines, one
 * per local/remote candidate-type combination, busiest first.
 *
 * Returns null when the report holds no candidate pairs at all — which is
 * itself worth seeing, because it means the ICE agent never got as far as
 * pairing anything.
 */
export function summarizeIceStats(report: unknown): string | null {
  if (!isMapLike(report)) return null;

  const candidateTypes = new Map<string, string>();
  const pairs: StatsEntry[] = [];

  report.forEach((value) => {
    if (typeof value !== 'object' || value === null) return;
    const entry = value as StatsEntry;
    const type = str(entry, 'type');
    if (type === 'local-candidate' || type === 'remote-candidate') {
      const id = str(entry, 'id');
      if (id) candidateTypes.set(id, str(entry, 'candidateType') ?? '?');
    } else if (type === 'candidate-pair') {
      pairs.push(entry);
    }
  });

  if (pairs.length === 0) {
    // Say what the report DID hold. "no pairs" on a peer that is still at
    // ice=checking is not a believable answer, and without this the next step
    // is another build cycle spent guessing whether the parser or the ICE
    // agent is at fault.
    const seen = new Map<string, number>();
    report.forEach((value) => {
      if (typeof value !== 'object' || value === null) return;
      const t = String((value as StatsEntry).type ?? 'untyped');
      seen.set(t, (seen.get(t) ?? 0) + 1);
    });
    if (seen.size === 0) return 'stats report was empty';
    return `no pairs; report held ${[...seen.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([t, n]) => `${t} x${n}`)
      .join(', ')}`;
  }

  // One bucket per local/remote type combination. The individual pairs are too
  // many to read on a phone — a board with a relay on both sides makes dozens.
  const buckets = new Map<
    string,
    { count: number; sent: number; got: number; succeeded: number }
  >();

  for (const pair of pairs) {
    const local =
      candidateTypes.get(str(pair, 'localCandidateId') ?? '') ?? '?';
    const remote =
      candidateTypes.get(str(pair, 'remoteCandidateId') ?? '') ?? '?';
    const key = `${local}/${remote}`;
    const bucket = buckets.get(key) ?? {
      count: 0,
      sent: 0,
      got: 0,
      succeeded: 0
    };
    bucket.count += 1;
    bucket.sent += num(pair, 'requestsSent');
    bucket.got += num(pair, 'responsesReceived');
    if (str(pair, 'state') === 'succeeded') bucket.succeeded += 1;
    buckets.set(key, bucket);
  }

  return [...buckets.entries()]
    .sort((a, b) => b[1].count - a[1].count)
    .map(
      ([key, b]) =>
        `${key} ${b.count} sent ${b.sent} got ${b.got}` +
        (b.succeeded > 0 ? ` ok ${b.succeeded}` : '')
    )
    .join(' · ');
}
